import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { format } from "date-fns";
import { 
  Plus, 
  Calendar as CalendarIcon, 
  Search, 
  FolderKanban,
  ArrowRight,
  Copy,
  Trash2,
  MoreVertical
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export default function ProjectsPage() {
  const { user, token, API } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);
  const [formData, setFormData] = useState({ product: "", location: "", target_date: "" });
  const [copyData, setCopyData] = useState({ source_project_id: "", new_product: "", new_location: "", new_target_date: "" });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, productsRes] = await Promise.all([
        fetch(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/products`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (projectsRes.ok) setProjects(await projectsRes.json());
      if (productsRes.ok) { const data = await productsRes.json(); setProducts(data.products || []); }
    } catch (error) { toast.error("Failed to load projects"); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.product || !formData.location) { toast.error("Please fill all required fields"); return; }
    try {
      const response = await fetch(`${API}/projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...formData, target_date: formData.target_date || null })
      });
      if (!response.ok) { const err = await response.json(); throw new Error(err.detail || "Failed"); }
      toast.success("Project created");
      setShowDialog(false);
      setFormData({ product: "", location: "", target_date: "" });
      fetchData();
    } catch (error) { toast.error(error.message); }
  };

  const handleCopyProject = async (e) => {
    e.preventDefault();
    if (!copyData.source_project_id || !copyData.new_product || !copyData.new_location) { toast.error("Please fill all required fields"); return; }
    try {
      const response = await fetch(`${API}/projects/copy`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...copyData, new_target_date: copyData.new_target_date || null })
      });
      if (!response.ok) { const err = await response.json(); throw new Error(err.detail || "Failed"); }
      const result = await response.json();
      toast.success(result.message);
      setShowCopyDialog(false);
      setCopyData({ source_project_id: "", new_product: "", new_location: "", new_target_date: "" });
      fetchData();
    } catch (error) { toast.error(error.message); }
  };

  const handleDeleteProject = async (projectId) => {
    if (!confirm("Delete this project and all its tasks?")) return;
    try {
      const response = await fetch(`${API}/projects/${projectId}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error("Failed to delete");
      toast.success("Project deleted");
      fetchData();
    } catch (error) { toast.error(error.message); }
  };

  const openCopyDialog = (project) => {
    setSelectedProject(project);
    setCopyData({ source_project_id: project.id, new_product: project.product, new_location: "", new_target_date: "" });
    setShowCopyDialog(true);
  };

  const filteredProjects = projects.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.product.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-slate-500">Loading projects...</div></div>;

  return (
    <div className="space-y-4" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900">Projects</h1>
          <p className="text-xs text-slate-500 mt-0.5">Manage preopening projects</p>
        </div>
        {user?.role === 'admin' && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowCopyDialog(true)} className="rounded gap-1 border-slate-300 h-8 text-xs" data-testid="copy-project-btn" disabled={projects.length === 0}>
              <Copy className="w-3.5 h-3.5" />Copy
            </Button>
            <Button size="sm" onClick={() => { setFormData({ product: "", location: "", target_date: "" }); setShowDialog(true); }} className="rounded gap-1 bg-[#1e3a5f] hover:bg-[#152a47] text-white h-8 text-xs" data-testid="add-project-btn">
              <Plus className="w-4 h-4" />New Project
            </Button>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
        <Input placeholder="Search projects..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-8 rounded h-9 border-slate-200 text-sm" data-testid="search-input" />
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredProjects.length === 0 ? (
          <Card className="col-span-full bg-white border-slate-200">
            <CardContent className="py-8 text-center">
              <FolderKanban className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No projects found</p>
              {user?.role === 'admin' && (
                <Button onClick={() => setShowDialog(true)} className="mt-3 bg-[#1e3a5f] hover:bg-[#152a47] text-white text-sm">Create First Project</Button>
              )}
            </CardContent>
          </Card>
        ) : (
          filteredProjects.map((project) => (
            <Card key={project.id} className="bg-white border-slate-200 hover:border-[#1e3a5f]/30 transition-all cursor-pointer group" data-testid={`project-card-${project.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 cursor-pointer" onClick={() => navigate(`/projects/${project.id}`)}>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-8 h-8 bg-[#1e3a5f]/10 rounded flex items-center justify-center shrink-0">
                        <FolderKanban className="w-4 h-4 text-[#1e3a5f]" />
                      </div>
                      <Badge variant="outline" className={`rounded text-xs ${project.completion_rate === 100 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : project.completion_rate > 50 ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                        {project.completion_rate}%
                      </Badge>
                    </div>
                    <h3 className="font-semibold text-sm text-slate-900 group-hover:text-[#1e3a5f] transition-colors">{project.name}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{project.product} &bull; {project.location}</p>
                  </div>
                  {user?.role === 'admin' && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="rounded h-7 w-7 text-slate-400 hover:text-slate-700">
                          <MoreVertical className="w-3.5 h-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white border-slate-200">
                        <DropdownMenuItem onClick={() => openCopyDialog(project)} className="text-slate-700 cursor-pointer text-xs">
                          <Copy className="w-3.5 h-3.5 mr-2" />Copy
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDeleteProject(project.id)} className="text-red-600 cursor-pointer text-xs">
                          <Trash2 className="w-3.5 h-3.5 mr-2" />Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
                <div className="cursor-pointer" onClick={() => navigate(`/projects/${project.id}`)}>
                  <div className="mb-2">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-500">Progress</span>
                      <span className="text-slate-500">{project.completed_tasks}/{project.total_tasks}</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#1e3a5f] rounded-full transition-all duration-500" style={{ width: `${project.completion_rate}%` }} />
                    </div>
                  </div>
                  {project.target_date && (
                    <div className="flex items-center gap-1.5 text-xs text-slate-500">
                      <CalendarIcon className="w-3 h-3" />
                      <span>Target: {new Date(project.target_date).toLocaleDateString('en-GB', {day:'2-digit',month:'short',year:'numeric'})}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-end mt-2 text-[#1e3a5f] group-hover:translate-x-1 transition-transform">
                    <span className="text-xs mr-1">View Tasks</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Project Dialog - using native date input for iOS */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md bg-white border-slate-200" data-testid="project-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900">Create New Project</DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">Create a preopening project</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-slate-700 text-sm">Product *</Label>
              <Select value={formData.product} onValueChange={(value) => setFormData({ ...formData, product: value })}>
                <SelectTrigger className="rounded-lg h-10 border-slate-200" data-testid="project-product-select"><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {products.map(product => (<SelectItem key={product} value={product}>{product}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-700 text-sm">Location *</Label>
              <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} placeholder="e.g., Radisson, Marriott" className="rounded-lg h-10 border-slate-200" data-testid="project-location-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-700 text-sm">Target Date</Label>
              <Input type="date" value={formData.target_date} onChange={(e) => setFormData({ ...formData, target_date: e.target.value })} className="rounded-lg h-10 border-slate-200" data-testid="project-date-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="rounded-lg border-slate-300">Cancel</Button>
              <Button type="submit" className="rounded-lg bg-[#1e3a5f] hover:bg-[#152a47] text-white" data-testid="save-project-btn">Create Project</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Copy Project Dialog */}
      <Dialog open={showCopyDialog} onOpenChange={setShowCopyDialog}>
        <DialogContent className="max-w-md bg-white border-slate-200" data-testid="copy-project-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900">Copy Project</DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">Copy all tasks from an existing project</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCopyProject} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-slate-700 text-sm">Source Project *</Label>
              <Select value={copyData.source_project_id} onValueChange={(value) => setCopyData({ ...copyData, source_project_id: value })}>
                <SelectTrigger className="rounded-lg h-10 border-slate-200" data-testid="copy-source-project"><SelectValue placeholder="Select source" /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {projects.map(p => (<SelectItem key={p.id} value={p.id}>{p.name} ({p.total_tasks} tasks)</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-700 text-sm">New Product *</Label>
              <Select value={copyData.new_product} onValueChange={(value) => setCopyData({ ...copyData, new_product: value })}>
                <SelectTrigger className="rounded-lg h-10 border-slate-200" data-testid="copy-new-product"><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {products.map(product => (<SelectItem key={product} value={product}>{product}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-700 text-sm">New Location *</Label>
              <Input value={copyData.new_location} onChange={(e) => setCopyData({ ...copyData, new_location: e.target.value })} placeholder="e.g., Radisson" className="rounded-lg h-10 border-slate-200" data-testid="copy-new-location" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-700 text-sm">New Target Date</Label>
              <Input type="date" value={copyData.new_target_date} onChange={(e) => setCopyData({ ...copyData, new_target_date: e.target.value })} className="rounded-lg h-10 border-slate-200" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCopyDialog(false)} className="rounded-lg border-slate-300">Cancel</Button>
              <Button type="submit" className="rounded-lg bg-[#1e3a5f] hover:bg-[#152a47] text-white" data-testid="copy-project-submit-btn">Copy Project</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}